import React from 'react'
import JiraXLogo from '../img/android-chrome-192x192.png'

interface LandingPageProps {
  onLogin: () => void
}

const LandingPage: React.FC<LandingPageProps> = ({ onLogin }) => {
  // Detect if we are running the SPA on localhost/127.* so we can
  // present a friendlier “dev mode” message instead of Microsoft login.
  const isLocalhost = /^localhost$|^127(\.\d+\.)?\d+$/.test(
    window.location.hostname
  )

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-100 flex flex-col items-center justify-center p-4">
      <div className="max-w-3xl w-full bg-white rounded-xl shadow-xl overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Left side - branding */}
          <div className="bg-indigo-600 text-white p-8 md:w-2/5 flex flex-col justify-center items-center">
            <div className="text-center">
              {/* Logo */}
              <img
                src={JiraXLogo}
                alt="JiraX logo"
                className="mx-auto mb-4 h-12 w-12 rounded"
              />
              <h1 className="text-4xl font-bold tracking-tight mb-2">
                <span className="text-indigo-200">Jira</span>
                <span className="text-white">X</span>
              </h1>
              <p className="text-indigo-200 text-sm">Powered by The Development Factory</p>
            </div>
            <div className="mt-8 hidden md:block">
              <div className="bg-indigo-500/30 p-4 rounded-lg">
                <p className="text-sm italic text-indigo-100">
                  “Plans are nothing; planning is everything.” <br />– Dwight D. Eisenhower
                </p>
              </div>
            </div>
          </div>

          {/* Right side - content */}
          <div className="p-8 md:w-3/5">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Welcome to JiraX</h2>
            <p className="text-gray-600 mb-6">
              JiraX provides powerful analytics and reporting for your Jira projects.
              Track sprint progress, analyze team performance, and generate
              comprehensive reports to improve your development process.
            </p>

            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="ml-3 text-sm text-gray-600">Sprint analytics and performance tracking</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="ml-3 text-sm text-gray-600">Team contribution leaderboards</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="ml-3 text-sm text-gray-600">Comprehensive project reporting</p>
              </div>
            </div>

            <div className="mt-8">
              <button
                onClick={onLogin}
                className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.5a1 1 0 00-.8.4l-1.9 2.533a1 1 0 01-1.6 0L6.3 17.4a1 1 0 00-.8-.4H3a2 2 0 01-2-2V5zm5 4a1 1 0 100-2 1 1 0 000 2zm7 0a1 1 0 100-2 1 1 0 000 2zm-7 4a1 1 0 100-2 1 1 0 000 2zm7 0a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                {isLocalhost ? 'Continue in Development Mode' : 'Sign in with Microsoft'}
              </button>
              <p className="mt-2 text-xs text-center text-gray-500">
                {isLocalhost
                  ? 'Development mode detected – a mock user will be used locally.'
                  : 'Sign in with your Microsoft work account to access the application.'}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 text-xs text-gray-500">
        &copy; {new Date().getFullYear()} The Development Factory. All rights reserved.
      </div>
    </div>
  )
}

export default LandingPage
